import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { Loader2, Trash2, Upload, CheckCircle2, Building2, Plus } from "lucide-react";

const input = "w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm bg-white";
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseRecipients(text, fallbackAmount) {
  return text.split(/\r?\n/).map((ln) => ln.split(/[,\t;]/).map((c) => c.trim().replace(/^"|"$/g, ""))).filter((c) => c.some(Boolean))
    .map((cells) => {
      const email = cells.find((c) => EMAIL.test(c)) || "";
      const num = cells.find((c) => /^£?\d+(\.\d+)?$/.test(c));
      const name = cells.find((c) => c && c !== email && c !== num) || "";
      return { name, email, amount: num ? parseFloat(num.replace("£", "")) : fallbackAmount };
    }).filter((r) => r.email);
}

const Field = ({ label, children }) => <label className="block"><span className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</span>{children}</label>;

const RecipientsEditor = ({ rows, setRows, amount, setAmount }) => {
  const [paste, setPaste] = useState("");
  const add = (text) => { setRows([...rows, ...parseRecipients(text, amount)]); setPaste(""); };
  const onFile = (e) => { const f = e.target.files?.[0]; if (f) f.text().then(add); e.target.value = ""; };
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <Field label="Default amount per voucher (£)"><input type="number" min="5" max="1000" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} className={input} data-testid="corp-default-amount" /></Field>
        <label className="sm:col-span-2 gp-btn-outline rounded-xl py-2.5 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer" data-testid="corp-upload-csv">
          <Upload size={16} /> Upload a CSV (name, email, amount)<input type="file" accept=".csv,.txt" onChange={onFile} className="hidden" />
        </label>
      </div>
      <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3} className={input} data-testid="corp-paste-input"
        placeholder={"Or paste one person per line:\nJane Smith, jane@company.com, 50\njohn@company.com"} />
      <button type="button" disabled={!paste.trim()} onClick={() => add(paste)} data-testid="corp-add-recipients" className="gp-btn-outline rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-40"><Plus size={15} /> Add to list</button>
      {rows.length > 0 && (
        <div className="border border-gray-100 rounded-xl max-h-72 overflow-y-auto" data-testid="corp-recipient-list">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2 items-center px-3 py-2 border-b border-gray-50 text-sm">
              <input value={r.name} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="flex-1 min-w-0 outline-none" data-testid={`corp-row-name-${i}`} />
              <span className="flex-1 min-w-0 truncate text-[#5a6b80]">{r.email}</span>
              <input type="number" value={r.amount} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, amount: parseFloat(e.target.value) || 0 } : x))} className="w-20 outline-none text-right" data-testid={`corp-row-amount-${i}`} />
              <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-red-400" data-testid={`corp-row-remove-${i}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      <p className="text-sm text-[#16243b] font-medium" data-testid="corp-summary">{rows.length} recipients · £{total.toFixed(2)} total</p>
    </div>
  );
};

export default function CorporatePage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", po_number: "", billing_address: "", message: "", send_date: "" });
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState(50);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => { api.get(`/store/${slug}/corporate`).then(({ data }) => setInfo(data)).catch(() => setInfo({ enabled: false })); }, [slug]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!rows.length) return setErr("Add at least one recipient.");
    setBusy(true);
    try { setDone((await api.post(`/store/${slug}/corporate-order`, { ...f, recipients: rows, origin: window.location.origin })).data); }
    catch (ex) { const d = ex?.response?.data?.detail; setErr(typeof d === "string" ? d : "Please check every email address and amount."); }
    finally { setBusy(false); }
  };

  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (!info.enabled) return <div className="min-h-screen flex items-center justify-center text-[#5a6b80]" data-testid="corp-unavailable">Corporate ordering isn't available for this store.</div>;
  const b = info.business;
  const btn = { background: b.button_color || "#2ec9c9", color: b.button_text_color || "#fff" };
  return (
    <div className="min-h-screen bg-[#f6f8fb]" data-testid="corporate-page">
      <div style={{ background: b.brand_color || "#16243b" }} className="px-6 py-14">
        <div className="max-w-[880px] mx-auto text-white">
          {b.logo_url && <img src={b.logo_url} alt={b.name} className="h-14 object-contain mb-5" />}
          <p className="text-white/70 text-sm flex items-center gap-2"><Building2 size={16} /> Corporate gifting</p>
          <h1 className="text-4xl sm:text-5xl font-bold mt-2">{b.name} vouchers for your team</h1>
          <p className="text-white/80 mt-3 max-w-[560px]">Thank staff, clients or prize winners. Add your list and we'll send you one invoice. Each person gets their own voucher by email once it's paid.</p>
        </div>
      </div>
      <div className="max-w-[880px] mx-auto px-6 py-10">
        {done ? (
          <div className="bg-white rounded-2xl p-10 text-center shadow-[0_10px_30px_rgba(20,30,55,0.07)]" data-testid="corp-success">
            <CheckCircle2 className="mx-auto text-[#2ec9c9]" size={48} />
            <h2 className="text-2xl font-bold text-[#16243b] mt-4">Order received</h2>
            <p className="text-[#5a6b80] mt-2">Reference <span className="font-mono font-semibold" data-testid="corp-ref">{done.ref}</span> · {done.count} vouchers · £{done.total.toFixed(2)}</p>
            <p className="text-sm text-[#8a97a8] mt-2">{b.name} will email you an invoice shortly.</p>
            <Link to={`/store/${slug}`} className="inline-block mt-6 rounded-full px-6 py-2.5 font-medium" style={btn}>Back to store</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_10px_30px_rgba(20,30,55,0.07)] space-y-8">
            <section className="grid sm:grid-cols-2 gap-4">
              <h2 className="sm:col-span-2 font-semibold text-[#16243b] text-lg">1. Your company</h2>
              <Field label="Company name *"><input required value={f.company_name} onChange={set("company_name")} className={input} data-testid="corp-company" /></Field>
              <Field label="Your name *"><input required value={f.contact_name} onChange={set("contact_name")} className={input} data-testid="corp-contact" /></Field>
              <Field label="Email for the invoice *"><input required type="email" value={f.contact_email} onChange={set("contact_email")} className={input} data-testid="corp-email" /></Field>
              <Field label="Phone"><input value={f.contact_phone} onChange={set("contact_phone")} className={input} data-testid="corp-phone" /></Field>
              <Field label="PO number"><input value={f.po_number} onChange={set("po_number")} className={input} data-testid="corp-po" /></Field>
              <Field label="Send vouchers on (optional)"><input type="date" value={f.send_date} onChange={set("send_date")} className={input} data-testid="corp-send-date" /></Field>
              <div className="sm:col-span-2"><Field label="Billing address"><textarea rows={2} value={f.billing_address} onChange={set("billing_address")} className={input} data-testid="corp-address" /></Field></div>
            </section>
            <section className="space-y-3">
              <h2 className="font-semibold text-[#16243b] text-lg">2. Who gets a voucher</h2>
              <RecipientsEditor rows={rows} setRows={setRows} amount={amount} setAmount={setAmount} />
            </section>
            <section>
              <h2 className="font-semibold text-[#16243b] text-lg mb-3">3. Message (optional)</h2>
              <textarea rows={3} value={f.message} onChange={set("message")} maxLength={500} placeholder="e.g. Thank you for a brilliant year!" className={input} data-testid="corp-message" />
            </section>
            {err && <p className="text-red-500 text-sm" data-testid="corp-error">{err}</p>}
            <button disabled={busy} data-testid="corp-submit-btn" className="w-full rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2" style={btn}>
              {busy && <Loader2 className="animate-spin" size={18} />} Request invoice
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
